import { Gavel, Users, CalendarBlank, Warning } from '@phosphor-icons/react';
import type { RawScheduleEvent, ConflictInfo } from '@/lib/useBarristerBusy';

interface Props {
  /** Events on this day (from useBarristerBusy). */
  events: RawScheduleEvent[];
  /** ISO datetime of the proposed conference start. Null = no selection yet. */
  proposedStart: string | null;
  /** Duration in minutes of the proposed conference. */
  proposedDurationMinutes: number;
  /** Conflicts already computed by getConflicts(). */
  conflicts: ConflictInfo[];
  /** Render times as wall-clock-in-UTC (calendar/diary convention). Default false (local). */
  utc?: boolean;
  /** Label for the proposed block — the kind of thing being scheduled. Default "Conference". */
  proposedLabel?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────
// Span 07:00–23:00 so early-morning and evening events (e.g. a 21:30 meeting) fall INSIDE the
// window rather than jammed against an edge — the strip was 08:00–20:00 and clipped anything later.
export const DAY_START_H = 7;   // 07:00
export const DAY_END_H   = 23;  // 23:00
const TOTAL_MIN   = (DAY_END_H - DAY_START_H) * 60;

// `utc`: read the instant as wall-clock-in-UTC (the calendar/diary convention — events are stored
// `${date}T${time}:00Z` and read via getUTCHours). Callers on that convention pass utc so a 13:00
// slot renders at 13:00, not shifted by the browser offset. Conference flows (true local instants)
// leave it false. Exported for deterministic timezone tests.
export function minutesFromDayStart(iso: string, utc = false): number {
  const d = new Date(iso);
  const h = utc ? d.getUTCHours() : d.getHours();
  const m = utc ? d.getUTCMinutes() : d.getMinutes();
  return h * 60 + m - DAY_START_H * 60;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function pct(minutes: number): string {
  return `${clamp((minutes / TOTAL_MIN) * 100, 0, 100)}%`;
}

export function fmtTime(iso: string, utc = false): string {
  return new Date(iso).toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit', ...(utc ? { timeZone: 'UTC' } : {}),
  });
}

export function fmtDate(iso: string, utc = false): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', ...(utc ? { timeZone: 'UTC' } : {}),
  });
}

// ─── Event type styling ───────────────────────────────────────────────────────
interface EventStyle {
  bar: string;
  badge: string;
  label: string;
  icon: React.ReactNode;
}

function getEventStyle(eventType: string): EventStyle {
  switch (eventType) {
    case 'hearing':
      return {
        bar: 'bg-brand-600/90',
        badge: 'bg-brand-50 text-brand-700 border border-brand-200 dark:bg-brand-900/30 dark:text-brand-300 dark:border-brand-700/50',
        label: 'Hearing',
        icon: <Gavel size={10} weight="fill" />,
      };
    case 'conference':
    case 'consultation':
    case 'meeting':
      return {
        bar: 'bg-amber-500/80',
        badge: 'bg-amber-50 text-amber-800 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700/50',
        label: 'Conference',
        icon: <Users size={10} weight="fill" />,
      };
    default:
      return {
        bar: 'bg-emerald-500/70',
        badge: 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700/50',
        label: 'Appointment',
        icon: <CalendarBlank size={10} weight="fill" />,
      };
  }
}

// ─── Hour tick marks ──────────────────────────────────────────────────────────
const TICK_HOURS = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];

export function BarristerDayTimeline({ events, proposedStart, proposedDurationMinutes, conflicts, utc = false, proposedLabel = 'Conference' }: Props) {
  const dateLabel = proposedStart
    ? fmtDate(proposedStart, utc)
    : events.length > 0
    ? fmtDate(events[0]!.startAt, utc)
    : '';

  const hasProposed = Boolean(proposedStart);
  const proposedStartMin = proposedStart ? minutesFromDayStart(proposedStart, utc) : 0;
  const hasConflicts = conflicts.length > 0;

  const conflictEventIds = new Set(conflicts.map((c) => c.event.id));

  return (
    <div className="rounded-md border border-border bg-card">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <span className="text-xs font-semibold text-foreground">{dateLabel}</span>
        {hasConflicts ? (
          <span className="flex items-center gap-1 text-[10px] font-semibold text-red-600 dark:text-red-400">
            <Warning size={10} weight="fill" />
            {conflicts.filter((c) => c.severity === 'blocking').length > 0 ? 'Hearing conflict' : 'Schedule conflict'}
          </span>
        ) : hasProposed ? (
          <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">Slot is clear</span>
        ) : null}
      </div>

      {/* Timeline bar */}
      <div className="px-3 pt-3 pb-1">
        <div className="relative h-10 rounded overflow-hidden bg-muted/30">
          {/* Hour grid lines */}
          {TICK_HOURS.map((h) => (
            <div
              key={h}
              className="absolute top-0 bottom-0 border-l border-border/40"
              style={{ left: pct((h - DAY_START_H) * 60) }}
            />
          ))}

          {/* Existing events */}
          {events.map((e) => {
            const startMin = minutesFromDayStart(e.startAt, utc);
            const endMin = minutesFromDayStart(e.endAt, utc);
            const durMin = Math.max(15, endMin - startMin); // min 15 min for visibility
            const style = getEventStyle(e.eventType);
            const isConflicting = conflictEventIds.has(e.id);

            return (
              <div
                key={e.id}
                className={`absolute top-1 bottom-1 rounded-sm ${style.bar} ${isConflicting ? 'ring-2 ring-red-500 ring-offset-1' : ''}`}
                style={{ left: pct(startMin), width: pct(durMin) }}
                title={`${e.title} · ${fmtTime(e.startAt, utc)}–${fmtTime(e.endAt, utc)}`}
              />
            );
          })}

          {/* Proposed conference ghost block */}
          {hasProposed && (
            <div
              className={`absolute top-0.5 bottom-0.5 rounded border-2 ${
                hasConflicts
                  ? 'border-red-500 bg-red-200/40'
                  : 'border-brand-500 bg-brand-100/40'
              } border-dashed`}
              style={{ left: pct(proposedStartMin), width: pct(proposedDurationMinutes) }}
              title={`Proposed: ${proposedStart ? fmtTime(proposedStart, utc) : ''}–${proposedStart ? fmtTime(new Date(new Date(proposedStart).getTime() + proposedDurationMinutes * 60_000).toISOString(), utc) : ''}`}
            />
          )}
        </div>

        {/* Hour labels */}
        <div className="relative h-4 mt-0.5">
          {TICK_HOURS.filter((h) => h % 2 === 0).map((h) => (
            <span
              key={h}
              className="absolute text-[9px] text-muted-foreground/60 -translate-x-1/2"
              style={{ left: pct((h - DAY_START_H) * 60) }}
            >
              {h}:00
            </span>
          ))}
        </div>
      </div>

      {/* Event list */}
      {events.length > 0 && (
        <div className="border-t border-border divide-y divide-border/50">
          {events
            .slice()
            .sort((a, b) => a.startAt.localeCompare(b.startAt))
            .map((e) => {
              const style = getEventStyle(e.eventType);
              const isConflicting = conflictEventIds.has(e.id);
              const durationMin = Math.round(
                (new Date(e.endAt).getTime() - new Date(e.startAt).getTime()) / 60_000,
              );
              const durationLabel = durationMin >= 60
                ? `${Math.floor(durationMin / 60)}h${durationMin % 60 ? ` ${durationMin % 60}m` : ''}`
                : `${durationMin}m`;

              return (
                <div
                  key={e.id}
                  className={`flex items-start gap-2 px-3 py-2 text-xs ${isConflicting ? 'bg-red-50/50 dark:bg-red-500/10' : ''}`}
                >
                  <div className={`mt-0.5 shrink-0 flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold ${style.badge}`}>
                    {style.icon}
                    {style.label}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-medium text-foreground truncate">{e.title}</span>
                      {e.case && (
                        <span className="text-muted-foreground/70 text-[10px]">
                          · {e.case.caseNumber}
                        </span>
                      )}
                    </div>
                    <div className="text-muted-foreground/70 mt-0.5">
                      {fmtTime(e.startAt, utc)} – {fmtTime(e.endAt, utc)}
                      <span className="ml-1.5 text-muted-foreground/50">({durationLabel})</span>
                      {e.location && <span className="ml-1.5">· {e.location}</span>}
                    </div>
                    {isConflicting && (
                      <div className="mt-0.5 flex items-center gap-1 font-medium text-red-600 dark:text-red-400">
                        <Warning size={10} weight="fill" />
                        {e.eventType === 'hearing'
                          ? 'Hearing — barrister is in court at this time'
                          : 'Existing commitment at this time'}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

          {/* Proposed slot in list (only when there's something on the day) */}
          {hasProposed && proposedStart && (
            <div className={`flex items-start gap-2 px-3 py-2 text-xs ${hasConflicts ? 'bg-red-50/30 dark:bg-red-500/5' : 'bg-brand-50/30 dark:bg-brand-900/15'}`}>
              <div className={`mt-0.5 shrink-0 flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold border-2 border-dashed ${hasConflicts ? 'border-red-400 text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-500/10 dark:border-red-500/60' : 'border-brand-400 text-brand-700 bg-brand-50 dark:text-brand-300 dark:bg-brand-900/30 dark:border-brand-500/60'}`}>
                <CalendarBlank size={10} />
                Proposed
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-foreground">{proposedLabel}</div>
                <div className="text-muted-foreground/70 mt-0.5">
                  {fmtTime(proposedStart, utc)} – {fmtTime(new Date(new Date(proposedStart).getTime() + proposedDurationMinutes * 60_000).toISOString(), utc)}
                  <span className="ml-1.5 text-muted-foreground/50">({proposedDurationMinutes}m)</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {events.length === 0 && hasProposed && proposedStart && (
        <div className="flex items-start gap-2 border-t border-border px-3 py-2.5 text-xs bg-emerald-50/30 dark:bg-emerald-500/5">
          <div className="mt-0.5 shrink-0 flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold border-2 border-dashed border-brand-400 text-brand-700 bg-brand-50 dark:text-brand-300 dark:bg-brand-900/30 dark:border-brand-500/60">
            <CalendarBlank size={10} />
            Proposed
          </div>
          <div className="flex-1">
            <div className="font-medium text-foreground">{proposedLabel}</div>
            <div className="text-muted-foreground/70 mt-0.5">
              {fmtTime(proposedStart, utc)} – {fmtTime(new Date(new Date(proposedStart).getTime() + proposedDurationMinutes * 60_000).toISOString(), utc)}
              · No other commitments on this day
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
